import styled, { css, keyframes } from "styled-components";

/*
 * Novosti, v2 redizajn (s.5): istaknuta novost full-width,
 * uniformni grid, kategorija chipovi + search u URL-u.
 *
 * Animacije: intro naslova/filtera na mount, featured kartica sa
 * koreografisanim ulaskom, grid kartice per-ćelija na scroll (useReveal).
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

const labelIn = keyframes`
  from { opacity: 0; letter-spacing: 0.45em; }
  to { opacity: 1; letter-spacing: 0.15em; }
`;

const imageSettle = keyframes`
  from { transform: scale(1.08); }
  to { transform: scale(1); }
`;

export const Page = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 5rem;
`;

export const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
  animation: ${labelIn} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PageH1 = styled.h1`
  margin: 0 0 1.5rem;
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.08s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ------------------------------- filteri -------------------------------- */
export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.75rem;
  animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) 0.2s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 900px) {
    flex-wrap: wrap;
  }
`;

export const ChipsRail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  overflow-x: auto;
  scrollbar-width: none;
  flex: 1;
  min-width: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  /* chipovi ulete jedan za drugim */
  & > * {
    animation: ${fadeUp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 10 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${250 + i * 40}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  @media (max-width: 900px) {
    flex: 1 0 100%;
    order: -1;
  }
`;

export const FilterChip = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 34px;
  padding: 0 0.9rem;
  border-radius: var(--radius-pill);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  background: ${(p) => (p.$active ? "var(--brand-dim)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--brand)" : "var(--text-secondary)")};
  border: 1px solid
    ${(p) => (p.$active ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)")};

  &:hover {
    color: ${(p) => (p.$active ? "var(--brand)" : "var(--text-primary)")};
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    position: absolute;
    left: 11px;
    color: var(--text-muted);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    flex: 1;
  }
`;

export const SearchField = styled.input`
  height: 40px;
  width: 230px;
  box-sizing: border-box;
  padding: 0 0.9rem 0 2.2rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.88rem;
  font-family: var(--font-body);
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;

/* --------------------------- istaknuta novost ---------------------------- */
export const FeaturedCard = styled.article`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
  margin-bottom: 2rem;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;
  animation: ${fadeUp} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) 0.15s backwards;

  &:hover {
    border-color: var(--border-strong);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const FeaturedImage = styled.div`
  position: relative;
  min-height: 320px;
  background: var(--bg-elevated);
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* slika se "slegne" iz blagog zooma dok kartica ulazi */
    animation: ${imageSettle} 1.1s cubic-bezier(0.2, 0.8, 0.2, 1) 0.15s backwards;
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      animation: none;
    }
  }

  @media (max-width: 900px) {
    aspect-ratio: 16 / 10;
    min-height: 0;
  }
`;

export const FeaturedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 2rem;

  /* sadržaj ulazi stepenasto, malo posle kartice */
  & > * {
    animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 6 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${300 + i * 80}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  @media (max-width: 900px) {
    padding: 1.5rem;
  }
`;

export const FeaturedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

export const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid var(--border-strong);
  color: var(--text-primary);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const FeaturedDate = styled.span`
  color: var(--text-muted);
  font-size: 0.82rem;
`;

export const FeaturedOwner = styled.span`
  width: fit-content;
  color: var(--brand);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &:hover {
    text-decoration: underline;
  }
`;

export const FeaturedTitle = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1.02;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

export const FeaturedExcerpt = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.97rem;
  line-height: 1.65;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const FeaturedCtas = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: auto;
  padding-top: 1rem;
`;

export const GhostCta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 42px;
  padding: 0 1.1rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;

export const TicketCta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 42px;
  padding: 0 1.1rem;
  background: var(--brand);
  border: none;
  border-radius: var(--radius-control);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: var(--brand-hover);
  }
`;

/* ---------------------------------- grid --------------------------------- */
export const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* Ćelija grida sa scroll-reveal, wrapper oko deljene NewsCard komponente,
   pa kartica ostaje netaknuta; flex rasteže karticu na punu visinu reda */
export const RevealCell = styled.div`
  display: flex;
  flex-direction: column;

  & > * {
    flex: 1;
  }

  opacity: 0;

  ${(p) =>
    p.$visible &&
    css`
      opacity: 1;
      animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      animation-delay: calc(var(--i, 0) * 70ms);
    `}

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.25s backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 44px;
  padding: 0 1.5rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 1rem;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  svg {
    color: var(--text-muted);
    margin-bottom: 1rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 1rem;
    margin: 0 0 1.5rem;
  }
`;
